import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Archive, ArchiveRestore, Play, Info, Sparkles, HelpCircle } from "lucide-react";
import KPIEditorDialog from "@/components/KPIEditorDialog";
import KPIValueDialog from "@/components/KPIValueDialog";
import KPITemplatePicker from "@/components/KPITemplatePicker";
import Sparkline from "@/components/Sparkline";
import { useHashHighlight } from "@/lib/highlightRow";

// Two-pane KPIs page. Left: list of KPI definitions with sparklines.
// Right: detail panel with bigger chart, recent values, inline edit/remove.

const STATUS_TONE = {
  ok: "green", warning: "amber", critical: "red", unknown: "slate",
};
const SOURCE_TONE = {
  manual: "slate", csv_import: "blue", api: "purple",
  auto_computed: "green", external: "amber",
};

export default function KPIsPage() {
  const [kpis, setKpis] = useState([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [series, setSeries] = useState(null);

  const [editor, setEditor] = useState({ open: false, editing: null });
  const [valueDialog, setValueDialog] = useState({ open: false, editing: null });
  const [templatePicker, setTemplatePicker] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);

  const loadCommon = async () => {
    try {
      const [{ data: c }, { data: t }, { data: e }] = await Promise.all([
        api.get("/categories"),
        api.get("/teams"),
        api.get("/employees"),
      ]);
      setCategories(c); setTeams(t); setEmployees(e);
    } catch { /* ignore */ }
  };

  const loadKPIs = async () => {
    try {
      const { data } = await api.get(`/kpis?active_only=${activeOnly}`);
      setKpis(data);
      if (data.length > 0 && !data.find((k) => k.kpi_id === selectedId)) {
        setSelectedId(data[0].kpi_id);
      }
      if (data.length === 0) setSelectedId(null);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load");
    }
  };

  const loadSeries = async (kpiId) => {
    if (!kpiId) { setSeries(null); return; }
    try {
      const { data } = await api.get(`/kpis/${kpiId}/series`);
      setSeries(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  useEffect(() => { loadCommon(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadKPIs(); /* eslint-disable-next-line */ }, [activeOnly]);
  useEffect(() => { loadSeries(selectedId); /* eslint-disable-next-line */ }, [selectedId]);

  // deep-link from advisory popovers: #kpi={kpi_id}.
  // Selects the KPI in the left list when present and flashes the row.
  useHashHighlight({
    key: "kpi", testidPrefix: "kpi-list-row-", deps: [kpis],
    onMatch: (id) => {
      if (kpis.find((k) => k.kpi_id === id)) setSelectedId(id);
    },
  });

  const filtered = useMemo(() => {
    let xs = kpis;
    if (search) {
      const s = search.toLowerCase();
      xs = xs.filter((k) => k.name.toLowerCase().includes(s) || (k.key || "").includes(s));
    }
    if (scopeFilter !== "all") xs = xs.filter((k) => k.scope === scopeFilter);
    return xs;
  }, [kpis, search, scopeFilter]);

  const archive = async (k) => {
    try {
      if (k.is_active) {
        await api.delete(`/kpis/${k.kpi_id}`);
        toast.success("KPI archived");
      } else {
        await api.patch(`/kpis/${k.kpi_id}`, { is_active: true });
        toast.success("KPI restored");
      }
      loadKPIs();
      if (selectedId === k.kpi_id) loadSeries(k.kpi_id);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const deleteValue = async (v) => {
    if (!window.confirm("Remove this value?")) return;
    try {
      await api.delete(`/kpis/${selectedId}/values/${v.value_id}`);
      toast.success("Removed");
      loadSeries(selectedId);
      loadKPIs();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const onSavedKpi = () => {
    setEditor({ open: false, editing: null });
    loadKPIs();
    if (selectedId) loadSeries(selectedId);
  };

  const onSavedValue = () => {
    setValueDialog({ open: false, editing: null });
    if (selectedId) loadSeries(selectedId);
    loadKPIs();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            Reports
            <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] px-1.5 py-0.5 rounded bg-[#DBE7FE] border border-[#BFD3FB]"
              data-testid="kpi-advanced-badge">Advanced</span>
          </div>
          <h1 className="font-display text-4xl mt-1 flex items-center gap-2">
            KPIs
            <button type="button" onClick={() => setHelpOpen((v) => !v)}
              data-testid="kpi-help-btn"
              className="text-[#4B5773] hover:text-[#0B1A36]"
              aria-label="What are KPIs?">
              <HelpCircle className="h-5 w-5" />
            </button>
          </h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Track the numbers that matter to your operation, calls handled, absence,
            customer satisfaction, anything you want to watch over time. KPIs are optional;
            you can add them whenever you&apos;re ready.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setTemplatePicker(true)}
            data-testid="kpi-templates-btn"
            className="h-10 rounded-lg">
            <Sparkles className="h-4 w-4 mr-1" />Browse templates
          </Button>
          <Button onClick={() => setEditor({ open: true, editing: null })}
            data-testid="kpi-create-btn"
            className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-1" />New KPI
          </Button>
        </div>
      </div>

      {helpOpen && (
        <div data-testid="kpi-help-text"
          className="mb-5 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-sm text-[#1F2A44]">
          <div className="font-medium text-[#0B1A36] mb-1">What&apos;s a KPI?</div>
          <p className="mb-2">
            A KPI (Key Performance Indicator) is any number you want to watch over time,             calls answered per day, absence percentage, customer satisfaction. NextRota stores
            what you record, charts the trend, and alerts you when it drifts from your target.
          </p>
          <p className="text-xs text-[#4B5773]">
            KPIs are optional and never change your rota, leave decisions, billing or scheduling.
            They&apos;re tracking tools only.
          </p>
        </div>
      )}

      {kpis.length === 0 ? (
        <Card data-testid="kpi-first-run">
          <div className="py-8 text-center max-w-xl mx-auto">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#DBE7FE] text-[#1D4ED8] mb-4">
              <Sparkles className="h-6 w-6" />
            </div>
            <h2 className="font-display text-2xl text-[#0B1A36]">Start tracking what matters</h2>
            <p className="text-sm text-[#4B5773] mt-2">
              Pick from our ready-made templates by industry, Customer Support, Warehouse, Healthcare,
              Retail or General Workforce, or define a fully custom KPI from scratch. You can always
              add more later, archive ones you stop needing, or skip this page entirely if you only
              need rota management.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
              <Button onClick={() => setTemplatePicker(true)}
                data-testid="kpi-empty-templates-btn"
                className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                <Sparkles className="h-4 w-4 mr-1" />Browse templates
              </Button>
              <Button variant="outline" onClick={() => setEditor({ open: true, editing: null })}
                data-testid="kpi-empty-create-btn"
                className="h-10 rounded-lg">
                <Plus className="h-4 w-4 mr-1" />Create a custom KPI
              </Button>
            </div>
            <p className="text-[11px] text-[#4B5773] mt-4">
              Not ready yet? You can come back any time, KPIs are optional.
            </p>
          </div>
        </Card>
      ) : (
      <>
      <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44] mb-5"
        data-testid="kpi-disclaimer">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#2563EB]" />
        <div>
          You record KPI values manually for now, CSV import and API are on the way.
          KPIs are tracking tools; they never affect your rota, leave decisions or billing.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-3">
          <div className="flex gap-2">
            <Input placeholder="Search…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="kpi-search"
              className="h-10 rounded-lg" />
            <Select value={scopeFilter} onValueChange={setScopeFilter}>
              <SelectTrigger className="h-10 w-32 rounded-lg"
                data-testid="kpi-scope-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="category">Category</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#4B5773]">
            <input type="checkbox" checked={activeOnly} data-testid="kpi-active-only"
              onChange={(e) => setActiveOnly(e.target.checked)}
              className="accent-[#2563EB]" />
            Active only
          </label>

          <Card className="!p-0 overflow-hidden">
            <ul className="divide-y divide-slate-100 max-h-[600px] overflow-auto"
                data-testid="kpi-list">
              {filtered.length === 0 && (
                <li className="px-4 py-10 text-center text-sm text-[#4B5773]"
                    data-testid="kpi-empty">
                  No KPIs match.
                </li>
              )}
              {filtered.map((k) => (
                <li key={k.kpi_id} data-testid={`kpi-list-row-${k.kpi_id}`}
                    onClick={() => setSelectedId(k.kpi_id)}
                    className={`px-4 py-3 cursor-pointer ${
                      selectedId === k.kpi_id ? "bg-[#F0F6FF]" : "hover:bg-slate-50"
                    } ${k.is_active ? "" : "opacity-60"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-[#0B1A36] truncate">{k.name}</div>
                      <div className="text-[11px] text-[#4B5773] flex items-center gap-1.5">
                        <Badge tone="slate">{k.scope}</Badge>
                        {k.unit && <span className="font-mono">{k.unit}</span>}
                        {k.scope_name && <span className="truncate">· {k.scope_name}</span>}
                      </div>
                    </div>
                    <KPIStatusPill direction={k.direction}
                      latest={k.latest_value} target={k.target_value}
                      warn={k.warning_threshold} crit={k.critical_threshold} />
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="lg:col-span-2">
          {series ? (
            <DetailPanel
              series={series}
              onEditKPI={() => setEditor({ open: true, editing: series.definition })}
              onArchive={() => archive(series.definition)}
              onRecord={() => setValueDialog({ open: true, editing: null })}
              onEditValue={(v) => setValueDialog({ open: true, editing: v })}
              onDeleteValue={deleteValue}
            />
          ) : (
            <Card>
              <div className="py-12 text-center text-sm text-[#4B5773]"
                   data-testid="kpi-no-selection">
                Pick a KPI from the list to see its trend, summary and values.
              </div>
            </Card>
          )}
        </div>
      </div>
      </>
      )}

      <KPIEditorDialog
        open={editor.open}
        onOpenChange={(v) => setEditor((p) => ({ ...p, open: v }))}
        editing={editor.editing}
        teams={teams} categories={categories} employees={employees}
        onSaved={onSavedKpi} />
      <KPIValueDialog
        open={valueDialog.open}
        onOpenChange={(v) => setValueDialog((p) => ({ ...p, open: v }))}
        kpi={series?.definition || null}
        editing={valueDialog.editing}
        onSaved={onSavedValue} />

      <KPITemplatePicker
        open={templatePicker}
        onOpenChange={setTemplatePicker}
        existingKeys={kpis.map((k) => k.key)}
        onCreated={loadKPIs} />
    </div>
  );
}

function DetailPanel({ series, onEditKPI, onArchive, onRecord, onEditValue, onDeleteValue }) {
  const d = series.definition;
  const s = series.summary;
  // last 12 points for the bigger chart
  const sparkPoints = (series.points || []).slice(-12);
  return (
    <div className="space-y-4" data-testid="kpi-detail">
      <Card>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-2xl">{d.name}</h2>
              <Badge tone="slate">{d.scope}{d.scope_name ? ` · ${d.scope_name}` : ""}</Badge>
              {!d.is_active && <Badge tone="slate">Archived</Badge>}
            </div>
            <div className="text-[11px] text-[#4B5773] mt-1 font-mono">{d.key}</div>
            {d.description && (
              <p className="text-sm text-[#4B5773] mt-2">{d.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onEditKPI}
              data-testid="kpi-edit-btn" className="h-9 rounded-lg">
              <Pencil className="h-4 w-4 mr-1" />Edit
            </Button>
            <Button variant="outline" onClick={onArchive}
              data-testid="kpi-archive-btn" className="h-9 rounded-lg">
              {d.is_active
                ? <><Archive className="h-4 w-4 mr-1" />Archive</>
                : <><ArchiveRestore className="h-4 w-4 mr-1" />Restore</>}
            </Button>
            {d.is_active && (
              <Button onClick={onRecord} data-testid="kpi-record-btn"
                className="h-9 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                <Plus className="h-4 w-4 mr-1" />Record value
              </Button>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Latest" value={fmt(s.latest, d.unit, d.currency_code)} testid="kpi-latest" />
          <Stat label="Mean" value={fmt(s.mean, d.unit, d.currency_code)} />
          <Stat label="Min / Max" value={`${fmt(s.min, d.unit, d.currency_code)} / ${fmt(s.max, d.unit, d.currency_code)}`} />
          <Stat label="vs target"
            value={s.vs_target_pct == null ? "-" : `${s.vs_target_pct}%`} />
        </div>

        <div className="mt-4">
          <div className="text-[11px] text-[#4B5773] mb-2">Trend (last 12 points)</div>
          <div className="rounded-lg border border-slate-200 p-3 bg-white">
            <Sparkline
              points={sparkPoints}
              width={600} height={120}
              showDots
              testid="kpi-trend-chart" />
          </div>
        </div>
      </Card>

      <Card>
        <div className="eyebrow mb-3">Recent values</div>
        <table className="w-full text-sm" data-testid="kpi-values-table">
          <thead>
            <tr className="text-left text-[#4B5773]">
              <th className="py-2 eyebrow font-normal">Period</th>
              <th className="py-2 eyebrow font-normal">Range</th>
              <th className="py-2 eyebrow font-normal">Value</th>
              <th className="py-2 eyebrow font-normal">Source</th>
              <th className="py-2 eyebrow font-normal">Note</th>
              <th className="py-2 eyebrow font-normal text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(series.points || []).length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-[#4B5773]"
                       data-testid="kpi-values-empty">
                No values yet. Click "Record value" to add the first one.
              </td></tr>
            )}
            {(series.points || []).slice().reverse().map((v) => (
              <tr key={v.value_id} data-testid={`kpi-value-row-${v.value_id}`}>
                <td className="py-2 text-[#4B5773]">{v.period}</td>
                <td className="py-2 text-[#4B5773]">
                  {v.period_start}{v.period_end !== v.period_start ? ` → ${v.period_end}` : ""}
                </td>
                <td className="py-2 font-medium">
                  {fmt(v.value, d.unit, d.currency_code)}
                </td>
                <td className="py-2">
                  <Badge tone={SOURCE_TONE[v.source] || "slate"}>
                    {v.source === "csv_import" ? "CSV"
                      : v.source === "auto_computed" ? "AUTO"
                      : v.source}
                  </Badge>
                </td>
                <td className="py-2 text-[11px] text-[#4B5773] truncate max-w-xs">
                  {v.note || ""}
                </td>
                <td className="py-2 text-right space-x-1 whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => onEditValue(v)}
                    data-testid={`kpi-value-edit-${v.value_id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDeleteValue(v)}
                    data-testid={`kpi-value-remove-${v.value_id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {(d.target_history || []).length > 0 && (
        <Card>
          <div className="eyebrow mb-3">Target history</div>
          <ul className="space-y-1.5 text-xs text-[#4B5773]"
              data-testid="kpi-target-history">
            {d.target_history.slice(-5).reverse().map((h, i) => (
              <li key={i}>
                <span className="font-mono">{h.changed_at?.slice(0, 10)}</span>
                {", target "}
                <code>{h.before?.target_value ?? "-"}</code>
                {" → "}
                <code>{h.after?.target_value ?? "-"}</code>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-3 rounded-lg bg-[#F6F9FE] border border-slate-200">
      <div className="eyebrow">{label}</div>
      <div className="font-display text-xl mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}

function KPIStatusPill({ direction, latest, target, warn, crit }) {
  if (latest == null) return <Badge tone="slate">No data</Badge>;
  let s = "ok";
  if (direction === "higher_is_better") {
    if (crit != null && latest <= crit) s = "critical";
    else if (warn != null && latest <= warn) s = "warning";
  } else if (direction === "lower_is_better") {
    if (crit != null && latest >= crit) s = "critical";
    else if (warn != null && latest >= warn) s = "warning";
  } else if (direction === "target_band" && target != null) {
    const dev = Math.abs(latest - target);
    if (crit != null && dev >= crit) s = "critical";
    else if (warn != null && dev >= warn) s = "warning";
  }
  return <Badge tone={STATUS_TONE[s]}>{s}</Badge>;
}

function Badge({ tone = "slate", children }) {
  const TONES = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red:   "bg-red-50 text-red-700 border-red-200",
    blue:  "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
    purple:"bg-violet-50 text-violet-700 border-violet-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}

function fmt(v, unit, cc) {
  if (v == null) return "-";
  if (unit === "currency" && cc) return `${cc} ${Number(v).toFixed(2)}`;
  if (unit === "percent") return `${v}%`;
  if (unit === "hours") return `${v}h`;
  return String(v);
}
